import { useEffect } from 'react';
import { QueryClient, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { getUserPastReports } from '../../../apis/adminReports';
import { getUserId } from '../../../reducers/user/userReducer';
import ReportsEventEmitter, {
  MessageFnType,
  ReportsEvents,
} from '../utils/ReportsEventEmitter';
import {
  isDownloadableReportExtension,
  isValidReportDownloadUrl,
  triggerReportDownload,
} from '../utils/triggerReportDownload';

const PAST_REPORTS_QUERY_KEY = 'pastReportsList';
const POLL_INTERVAL_MS = 750;
const MAX_POLL_ATTEMPTS = 12;

interface CompletedPastReport {
  report_name: string;
  report_url: string;
  report_extension: string;
  status: string;
}

interface PastReportsResponse {
  results: CompletedPastReport[];
  url_ttl?: number;
}

const matchesReportName = (report: CompletedPastReport, reportName: string): boolean =>
  report.report_name === reportName || report.report_name.startsWith(`${reportName} `);

const findCompletedReport = (
  reports: CompletedPastReport[],
  reportName: string,
): CompletedPastReport | undefined =>
  reports.find(
    (report) => report.status === 'completed' && matchesReportName(report, reportName),
  );

const getPastReportsFromCache = (
  queryClient: QueryClient,
  userId: number | string | undefined,
): CompletedPastReport[] => {
  const data = queryClient.getQueryData<PastReportsResponse>([
    PAST_REPORTS_QUERY_KEY,
    userId,
  ]);
  return data?.results ?? [];
};

const fetchPastReportsData = async (): Promise<PastReportsResponse> => {
  const res = (await getUserPastReports()) as {
    response?: { data?: PastReportsResponse };
  };
  return res.response?.data || { results: [] };
};

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const refreshPastReportsList = async (
  queryClient: QueryClient,
  userId: number | string | undefined,
): Promise<void> => {
  if (!userId) {
    return;
  }

  await queryClient.fetchQuery([PAST_REPORTS_QUERY_KEY, userId], fetchPastReportsData, {
    staleTime: 0,
  });
};

const markReportCompletedInCache = (
  queryClient: QueryClient,
  userId: number | string | undefined,
  reportName: string,
): void => {
  if (!userId) {
    return;
  }

  queryClient.setQueryData<PastReportsResponse>(
    [PAST_REPORTS_QUERY_KEY, userId],
    (currentData) => {
      if (!currentData?.results?.length) {
        return currentData ?? { results: [] };
      }

      return {
        ...currentData,
        results: currentData.results.map((report) => {
          if (matchesReportName(report, reportName) && report.status === 'pending') {
            return { ...report, status: 'completed' };
          }
          return report;
        }),
      };
    },
  );
};

const markReportFailedInCache = (
  queryClient: QueryClient,
  userId: number | string | undefined,
  reportName: string,
): void => {
  if (!userId) {
    return;
  }

  queryClient.setQueryData<PastReportsResponse>(
    [PAST_REPORTS_QUERY_KEY, userId],
    (currentData) => {
      if (!currentData?.results?.length) {
        return currentData ?? { results: [] };
      }

      return {
        ...currentData,
        results: currentData.results.map((report) => {
          if (matchesReportName(report, reportName) && report.status === 'pending') {
            return { ...report, status: 'failed' };
          }
          return report;
        }),
      };
    },
  );
};

const isCompletedDownloadableReport = (report: CompletedPastReport): boolean =>
  isDownloadableReportExtension(report.report_extension);

const canAutoDownloadReport = (report: CompletedPastReport): boolean =>
  isValidReportDownloadUrl(report.report_url) &&
  isDownloadableReportExtension(report.report_extension);

const tryRefreshAndFindCompletedReport = async (
  queryClient: QueryClient,
  userId: number | string | undefined,
  reportName: string,
  attempt: number,
  maxAttempts: number,
): Promise<CompletedPastReport | null> => {
  await refreshPastReportsList(queryClient, userId);

  const report = findCompletedReport(
    getPastReportsFromCache(queryClient, userId),
    reportName,
  );

  if (report && isCompletedDownloadableReport(report)) {
    return report;
  }

  if (attempt >= maxAttempts - 1) {
    return null;
  }

  await delay(POLL_INTERVAL_MS);
  return tryRefreshAndFindCompletedReport(
    queryClient,
    userId,
    reportName,
    attempt + 1,
    maxAttempts,
  );
};

const fetchCompletedReport = (
  queryClient: QueryClient,
  userId: number | string | undefined,
  reportName: string,
): Promise<CompletedPastReport | null> =>
  tryRefreshAndFindCompletedReport(queryClient, userId, reportName, 0, MAX_POLL_ATTEMPTS);

const useWebSocketListener = () => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  useEffect(() => {
    const handleReportCompleted: MessageFnType = async (payload) => {
      markReportCompletedInCache(queryClient, userId, payload.report_name);

      try {
        const completedReport = await fetchCompletedReport(
          queryClient,
          userId,
          payload.report_name,
        );

        if (completedReport && canAutoDownloadReport(completedReport)) {
          triggerReportDownload(
            completedReport.report_url,
            payload.report_name,
            completedReport.report_extension,
          );
          toast.success(
            `Report "${payload.report_name}" has been generated and downloaded.`,
          );
          return;
        }

        if (completedReport) {
          toast.success(
            `Report "${payload.report_name}" has been generated successfully.`,
          );
          return;
        }

        toast.success(
          `Report "${payload.report_name}" has been generated successfully. Download it from Past Reports.`,
        );
      } finally {
        await refreshPastReportsList(queryClient, userId);
      }
    };

    const handleReportFailed: MessageFnType = async (payload) => {
      markReportFailedInCache(queryClient, userId, payload.report_name);
      await refreshPastReportsList(queryClient, userId);

      toast.error(
        `Report "${payload.report_name}" failed to generate. Please try again. If the problem persists, contact support.`,
      );
    };

    ReportsEventEmitter.on(
      ReportsEvents.SKILL_LEVEL_REPORT_COMPLETED,
      handleReportCompleted,
    );
    ReportsEventEmitter.on(ReportsEvents.SKILL_LEVEL_REPORT_FAILED, handleReportFailed);
    ReportsEventEmitter.on(
      ReportsEvents.PM_REPORT_COMPLETED,
      handleReportCompleted,
    );
    ReportsEventEmitter.on(
      ReportsEvents.PM_REPORT_FAILED,
      handleReportFailed,
    );

    return () => {
      ReportsEventEmitter.off(
        ReportsEvents.SKILL_LEVEL_REPORT_COMPLETED,
        handleReportCompleted,
      );
      ReportsEventEmitter.off(
        ReportsEvents.SKILL_LEVEL_REPORT_FAILED,
        handleReportFailed,
      );
      ReportsEventEmitter.off(
        ReportsEvents.PM_REPORT_COMPLETED,
        handleReportCompleted,
      );
      ReportsEventEmitter.off(
        ReportsEvents.PM_REPORT_FAILED,
        handleReportFailed,
      );
    };
  }, [queryClient, userId]);
};

export default useWebSocketListener;
